import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, getDefaultLanguageForDomain } from '@/components/LanguageLayout';

const DOMAIN_FOR_LANG: Record<string, string> = {
  sv: 'https://estylingstudio.se',
};
const getDomainForLang = (lang: string) => DOMAIN_FOR_LANG[lang] ?? 'https://estylingstudio.com';
const addTrailingSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`);

export const useLocalizedPath = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const defaultLang = getDefaultLanguageForDomain();
  
  const currentLang = lang && SUPPORTED_LANGUAGES.includes(lang) 
    ? lang 
    : (SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : defaultLang);

  // Build internal path for navigation
  const buildPath = (path: string) => {
    const cleanPath = path.replace(/^\/+/, '').replace(/\/+$/, '');
    if (!cleanPath) {
      return `/${currentLang}/`;
    }
    return `/${currentLang}/${cleanPath}/`;
  };

  // Build canonical URL for SEO - always without trailing slash except for root
  const getCanonicalUrl = (path: string = '') => {
    const cleanPath = path.replace(/^\/+/, '').replace(/\/+$/, '');
    const domain = getDomainForLang(currentLang);
    if (!cleanPath) {
      return addTrailingSlash(`${domain}/${currentLang}`);
    }
    return addTrailingSlash(`${domain}/${currentLang}/${cleanPath}`);
  };

  return { currentLang, buildPath, getCanonicalUrl, BASE_URL: getDomainForLang(currentLang) };
};

// Helper function for building URLs outside of React components
export const buildCanonicalUrl = (lang: string, path: string = '') => {
  const cleanPath = path.replace(/^\/+/, '').replace(/\/+$/, '');
  const domain = getDomainForLang(lang);
  if (!cleanPath) {
    return addTrailingSlash(`${domain}/${lang}`);
  }
  return addTrailingSlash(`${domain}/${lang}/${cleanPath}`);
};
